import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchEasykitOplijsterProjects } from '@/api/easykit-oplijster';
import type { EasykitOplijsterProject } from '@/api/easykit-oplijster';

const dateFormatter = new Intl.DateTimeFormat('nl-BE', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

function formatDate(dateStr: string | false): string {
  if (!dateStr) return '';
  // Odoo date_order can be "2026-01-28" or "2026-01-28 10:30:00"
  const dateOnly = dateStr.slice(0, 10);
  const d = new Date(dateOnly + 'T12:00:00');
  if (isNaN(d.getTime())) return '';
  return dateFormatter.format(d);
}

export function EasykitOplijsterView() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<EasykitOplijsterProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadProjects = useCallback(async (search: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchEasykitOplijsterProjects(search || undefined);
      setProjects(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kon projecten niet laden');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount and when debounced search changes
  useEffect(() => {
    loadProjects(debouncedSearch);
  }, [debouncedSearch, loadProjects]);

  const handleProjectClick = (project: EasykitOplijsterProject) => {
    navigate(`/project/${project.projectId}`);
  };

  return (
    <div>
      {/* Header */}
      <div className="px-4 lg:px-8 pt-4 lg:pt-6 pb-2 lg:pb-4">
        <h1 className="text-lg lg:text-xl font-extrabold text-td-navy text-center">
          EasyKit Oplijsting
        </h1>
        <p className="text-td-muted text-xs lg:text-sm mt-1 text-center">
          {!loading && !error && `${projects.length} projecten`}
        </p>
      </div>

      {/* Search */}
      <div className="px-4 lg:px-8 pb-3">
        <div className="max-w-2xl mx-auto relative">
          <svg
            className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <label htmlFor="oplijster-search" className="sr-only">
            Zoek project, klant of verkoopsorder
          </label>
          <input
            id="oplijster-search"
            type="text"
            placeholder="Zoek project, klant of verkoopsorder..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue focus:border-transparent"
          />
        </div>
      </div>

      {/* Content */}
      <div className="px-4 lg:px-8 pb-8 mt-3 lg:mt-5">
        {/* Loading state */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
              <span className="sr-only">Laden...</span>
            </div>
            <p className="text-td-muted text-sm mt-3">Projecten laden...</p>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
            <p className="font-semibold">Fout bij laden</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        )}

        {/* Data */}
        {!loading && !error && (
          <>
            {/* Empty state */}
            {projects.length === 0 && (
              <div className="text-center py-12">
                <p className="text-td-muted text-sm">
                  Geen EasyKit projecten gevonden
                  {searchInput && (
                    <span>
                      {' '}
                      voor &quot;{searchInput}&quot;
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Desktop table */}
            {projects.length > 0 && (
              <div className="hidden lg:block">
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-td-muted uppercase tracking-wider">Project</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-td-muted uppercase tracking-wider">Klant</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-td-muted uppercase tracking-wider">Verkoopsorder</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-td-muted uppercase tracking-wider">Datum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projects.map((project) => (
                        <tr
                          key={project.soId}
                          onClick={() => handleProjectClick(project)}
                          className="border-b border-gray-50 hover:bg-td-blue/5 transition-colors cursor-pointer"
                        >
                          <td className="px-4 py-3 text-sm font-semibold text-td-navy">{project.projectName}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{project.clientName}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{project.soName}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{formatDate(project.dateOrder)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Mobile cards */}
            {projects.length > 0 && (
              <div className="lg:hidden space-y-3 pb-20">
                {projects.map((project) => (
                  <button
                    key={project.soId}
                    type="button"
                    onClick={() => handleProjectClick(project)}
                    className="w-full text-left bg-white rounded-lg shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <p className="text-sm font-semibold text-td-navy">{project.projectName}</p>
                    <p className="text-sm text-gray-600 mt-0.5">{project.clientName}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-500">{project.soName}</p>
                      <p className="text-xs text-gray-400">{formatDate(project.dateOrder)}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
